import { put, call, select, takeLatest } from 'redux-saga/effects'
import { getWorkbenchMenus } from '@utils/menuUtils'
import {
  resetWorkbenchMenu,
  setMenuOrder,
  setSubMenuOrder,
  setSubMenuActive
} from '@actions/workbench'
import workbenchTypes from '@types/workbench'
import { getStore, setStore } from '@utils/storage'

const NAMESPACE = 'workbench.user.'

const isValidConfig = config =>
  config &&
  Array.isArray(config.menuOrder) &&
  config.subMenuOrder &&
  config.subMenuActive

const getShortcutMenus = menu =>
  getWorkbenchMenus(menu).filter(i => i.id !== 'system_setting_desk')

const buildShortcutLookup = menus =>
  menus.reduce(
    (result, menu) => ({
      ...result,
      [menu.id]: (menu.children || []).reduce(
        (children, child) => ({
          ...children,
          [child.id]: true
        }),
        {}
      )
    }),
    {}
  )

const hasCurrentShortcut = (shortcutLookup, menuId, subMenuId) =>
  !shortcutLookup ||
  (shortcutLookup[menuId] && shortcutLookup[menuId][subMenuId])

const hasActiveShortcut = (config, menus) => {
  // Stored workbench config can outlive menu ids after local data resets.
  const shortcutLookup = Array.isArray(menus)
    ? buildShortcutLookup(menus)
    : null

  return config.menuOrder.some(menuId =>
    (config.subMenuOrder[menuId] || []).some(subMenuId => {
      const active =
        config.subMenuActive[menuId] && config.subMenuActive[menuId][subMenuId]

      return active && hasCurrentShortcut(shortcutLookup, menuId, subMenuId)
    })
  )
}

export const isRestorableConfig = (config, menus) =>
  isValidConfig(config) &&
  config.menuOrder.length > 0 &&
  // A saved config with no current active shortcut renders an empty workbench.
  hasActiveShortcut(config, menus)

function* getNamespace() {
  try {
    const id = yield select(state => state.user.users.id)
    if (id === undefined || id === null || id === '') return false
    return [NAMESPACE, id].join('')
  } catch (error) {
    return false
  }
}

export function* saveWorkbenchMenu(menu) {
  if (menu) {
    const menuList = getShortcutMenus(menu)
    yield call(saveConfig, undefined, menuList)
    yield put(resetWorkbenchMenu(menuList))
    const config = yield select(state => state.workbench)

    const namespace = yield getNamespace()
    // Avoid writing anonymous workbench config before the user session is restored.
    if (namespace) {
      setStore(namespace, config, 'local')
    }
  }
}

export function* saveConfig(action, menus) {
  let config
  const namespace = yield getNamespace()
  // The workbench config is user-scoped; skip local storage until a user id exists.
  if (!namespace) return false
  if (action) {
    config = action.payload
  } else {
    config = getStore(namespace, 'local')
  }
  // Empty restored shortcuts usually mean stale local cache; rebuild defaults from menu data.
  if (!isRestorableConfig(config, menus)) return false
  if (action) {
    setStore(namespace, config, 'local')
  }
  const { menuOrder, subMenuOrder, subMenuActive } = config
  yield put(setMenuOrder(menuOrder))
  yield put(setSubMenuOrder(subMenuOrder))
  yield put(setSubMenuActive(subMenuActive))
  return true
}

export function* clearConfig() {
  yield put(setMenuOrder([]))
  yield put(setSubMenuOrder({}))
  yield put(setSubMenuActive({}))
}

export function* onLogout() {
  yield clearConfig()
}

export function* initWorkbench() {
  const menu = yield select(state => state.layout.menuData)
  const menuList = menu && menu.length ? getShortcutMenus(menu) : []
  const restored = yield call(saveConfig, undefined, menuList)
  if (restored) return

  if (menu && menu.length) {
    yield call(saveWorkbenchMenu, menu)
  }
}

export default function* workerSaga() {
  yield takeLatest(workbenchTypes.SET_MENU, saveConfig)
}
